import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Header } from '../common_v2/components/Header';
import {
    AddressLocation,
    Button,
    ButtonTheme,
    Panel,
    PanelBorderRadius,
    PanelTheme,
    useWaitOverlayStore,
} from '@apply/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import './scss/_reviewandsubmit.scss';
import { Signature } from '@apply/components/src/components/controls/Signature';
import { DrawType } from '@apply/constants';
import { useDeriveDrawBudgetDataFromDrawBudgetId } from '../home_v2/hooks/useDeriveDrawBudgetDataFromDrawBudgetId';
import { useAuthentication } from '../Login/hooks/useAuthentication';

export const MobileReviewAndSubmit: React.FC = () => {
    const { setWaiting } = useWaitOverlayStore.getState();

    const { authentication } = useAuthentication();
    const drawBudgetId = parseInt(location.pathname.split('/')[2]);
    const { drawBudget, latestDraw, drawLines, total, street, city, isLoading } =
        useDeriveDrawBudgetDataFromDrawBudgetId(drawBudgetId);

    const navigate = useNavigate();

    // Check if this is an approval scenario based on draw state and current user
    // (a) GC has signed, (b) GC signature requires approval, (c) Principal hasn't signed yet,
    // (d) Current user is the approving user
    const isApprovalScenario =
        latestDraw?.generalContractorSignatureId != null &&
        latestDraw?.generalContractorSignatureRequiresApproval === true &&
        latestDraw?.borrowerSignatureId == null &&
        drawBudget?.isCurrentUserDrawApprover === true;

    useEffect(() => {
        if (isLoading) {
            setWaiting(true, 'Loading...');
        } else {
            setWaiting(false);
        }
    }, [isLoading]);

    const drawSignPage = () => {
        if (isApprovalScenario) {
            // Navigate to sign page with approval flag
            navigate(`/mobiledrawsign/${drawBudgetId}/${latestDraw.id}?approve=true`);
        } else {
            navigate(`/mobiledrawsign/${drawBudgetId}`);
        }
    };

    const drawControlPageOrHome = () => {
        if (!latestDraw || !drawBudget) return;

        if (
            latestDraw.drawType === DrawType.Automatic ||
            latestDraw.borrowerSubmitted ||
            latestDraw.needsBorrowerSignature ||
            !drawBudget.isEquityMet ||
            drawBudget.isBudgetMaxed ||
            !drawBudget.isApproved ||
            drawBudget.isFunded
        ) {
            navigate('/home');
            return;
        }

        navigate(`/draw/${drawBudgetId}`);
    };

    const submitButtonText = () => {
        if (!latestDraw) return '';

        if (isApprovalScenario) return 'Approve';

        if (latestDraw?.borrowerRequested && latestDraw.needsBorrowerSignature) {
            return 'Sign Revised Draw';
        }

        return 'Review and Submit';
    };

    return (
        <div>
            <Header />
            <div className='reviewandsubmit'>
                {latestDraw?.borrowerSubmitted ? (
                    <h2>{`Submitted on: ${latestDraw?.borrowerRequested}`}</h2>
                ) : (
                    <h2>Review and Submit</h2>
                )}
                <div className='number'>Draw #{drawBudget?.drawCount}</div>
                <Panel
                    theme={PanelTheme.White}
                    borderRadius={PanelBorderRadius.Medium}
                    padding={4}
                >
                    <AddressLocation
                        addressLine1={street}
                        addressLine2={city}
                    />
                </Panel>
                <Panel
                    theme={PanelTheme.White}
                    borderRadius={PanelBorderRadius.Medium}
                    padding={8}
                >
                    <div className='body'>
                        {drawLines.map((dbi) => (
                            <div
                                key={dbi.label}
                                className='item'
                            >
                                <span className='label'>{dbi.label}</span>
                                <span className='value'>
                                    {dbi.value.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className='total'>
                        <span className='label'>Total Draw</span>
                        <span className='value'>
                            {total.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                        </span>
                    </div>
                </Panel>
                <div className='submit-button'>
                    <Button
                        id='primaryButton'
                        theme={ButtonTheme.LinkBlue}
                        width='250px'
                        handleClick={drawControlPageOrHome}
                    >
                        <span className='icon'>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </span>{' '}
                        &nbsp; Back
                    </Button>
                    {latestDraw?.borrowerSubmitted && !latestDraw.needsBorrowerSignature ? (
                        <Signature
                            firstName={authentication?.user.firstName ?? ''}
                            lastName={authentication?.user.lastName ?? ''}
                        />
                    ) : (
                        <Button
                            id='primaryButton'
                            theme={ButtonTheme.Primary}
                            width='314px'
                            handleClick={drawSignPage}
                        >
                            {submitButtonText()}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
